import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { AttributeTypeFacetModel, AttributeTypeModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListActions from "@insite/content-library/Widgets/ProductList/ProductListActions";
import ProductListProductContext from "@insite/content-library/Widgets/ProductList/ProductListProductContext";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { Data } from "@react-google-maps/api";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// XC Customization: Remove product images from product list table

interface OwnProps {
    products: ProductModel[];
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    productInfosByProductId: state.pages.productList.productInfosByProductId,
    visibleColumnNames: state.pages.productList.visibleColumnNames,
    tableColumns: state.pages.productList.tableColumns,
});

type Props = ReturnType<typeof mapStateToProps> & ProductCardSelections & OwnProps;

export interface ProductListTableStyles {
    wrapper?: InjectableCss;
    dataTable?: DataTableProps;
    descriptionHeader?: DataTableHeaderProps;
    attributesHeader?: DataTableHeaderProps;
    availabilityHeader?: DataTableHeaderProps;
    priceHeader?: DataTableHeaderProps;
    unitOfMeasureHeader?: DataTableHeaderProps;
    actionsHeader?: DataTableHeaderProps;
    descriptionCell?: DataTableCellProps;
    gridContainer?: GridContainerProps;
    productImageStyles?: ProductListProductImageStyles;
    attributesCell?: DataTableCellProps;
    availabilityCell?: DataTableCellProps;
    availabilityStyles?: ProductAvailabilityStyles;
    priceCell?: DataTableCellProps;
    productPrice?: ProductPriceStyles;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    unitOfMeasureCell?: DataTableCellProps;
    unitOfMeasureText?: TypographyPresentationProps;
    actionsCell?: DataTableCellProps;
    sponsoredText?: TypographyPresentationProps;
    productLink?: LinkPresentationProps;
    priceBreakCell?: DataTableCellProps;
    priceBreakHeader?: DataTableHeaderProps;
}

export const actionsStyles: ProductListTableStyles = {
    wrapper: {
        css: css`
            overflow: auto;
            margin: auto;
        `,
    },
    gridContainer: {
        gap: 0,
        css: css`
            padding: 30px 0;
        `,
    },
    productImageStyles: {
        compareCheckbox: {
            css: css`
                align-self: start;
                padding-top: 15px;
            `,
        },
    },
    actionsHeader: {
        css: css`
            position: sticky;
            right: 0;
            background-clip: padding-box;
        `,
    },
    descriptionCell: {
        css: css`
            white-space: normal;
        `,
    },
    attributesCell: {
        css: css`
            white-space: normal;
        `,
    },
    availabilityCell: {
        css: css`
            white-space: normal;
        `,
    },
    availabilityStyles: {
        messageText: {
            css: css`
                white-space: nowrap;
            `,
        },
    },
    actionsCell: {
        css: css`
            position: sticky;
            right: 0;
            background-color: inherit;
            background-clip: padding-box;
        `,
    },
    sponsoredText: {
        css: css`
            margin-bottom: 5px;
            font-size: 0.9em;
            opacity: 0.5;
        `,
    },

    dataTable: {
        css: css`
            th {
                background-color: rgb(251, 241, 200);
                border: 1px solid black;
                text-align: center;
            }
            td {
                border: 1px solid black;
                text-align: center;
            }
            tr:nth-child(even) {
                background-color: rgb(251, 241, 200);
            }
            border-color: black;
            margin-top: 15px;
        `,
    },
    priceBreakCell: {
        css: css`
            border-left: none !important;
            border-right: none !important;
        `,
    },
    priceBreakHeader: {
        css: css`
            border-left: none !important;
            border-right: none !important;
        `,
    },
    productLink: {
        typographyProps: {
            underline: true,
        },
    },
};

const styles = actionsStyles;

const ProductListTable = ({
    products,
    productSettings,
    productInfosByProductId,
    visibleColumnNames,
    tableColumns,
    showAvailability,
    showBrand,
    showPartNumbers,
    showTitle,
    showAddToList,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
}: Props) => {
    const getAttributeValueForSection = (facet: AttributeTypeFacetModel, product: ProductModel) => {
        const attributeValues = product.attributeTypes
            ?.find(o => o.id === facet.attributeTypeId)
            ?.attributeValues?.map(o => o.valueDisplay)
            .join(", ");
        if (attributeValues) {
            return attributeValues;
        }
        if (facet.name === "Brand" && product.brand) {
            return product.brand.name;
        }
        if (facet.name === "Product Line" && product.productLine) {
            return product.productLine.name;
        }

        return null;
    };

    // XC Customization: Generates attribute and pricing headers based off first product in result list
    const firstProductPricing = productInfosByProductId[products[0].id]?.pricing?.actualBreakPrices;
    const priceBreakHeaders: string[] = [];
    if (firstProductPricing) {
        firstProductPricing.forEach(price => {
            priceBreakHeaders.push(price.breakQty.toString());
        });
        priceBreakHeaders[priceBreakHeaders.length - 1] = `${priceBreakHeaders[priceBreakHeaders.length - 1]}+`;
    }
    const priceHeaderRowSpan = priceBreakHeaders.length <= 1 ? 1 : 2;

    let topPriceHeader = (
        <DataTableHeader>
            <></>
        </DataTableHeader>
    );
    // const priceHeaderProp = products[0].properties["table_Price_Break_UoM"];
    // if (priceBreakHeaders.length > 1) {
    //     topPriceHeader = <DataTableHeader colSpan={priceBreakHeaders.length}>{priceHeaderProp}</DataTableHeader>;
    // }
    topPriceHeader = <DataTableHeader colSpan={priceBreakHeaders.length}>Price Per Box</DataTableHeader>;

    const getPriceBreaksForRow = (priceBreak: string, product: ProductModel) => {
        const productInfo = productInfosByProductId[product.id];
        if (!productInfo) {
            return null;
        }
        const priceBreaks = productInfo.pricing?.actualBreakPrices;
        if (priceBreaks) {
            const priceBreakValue = priceBreaks.find(o => o.breakQty.toString() === priceBreak.replace("+", ""));
            if (priceBreakValue) {
                return priceBreakValue.breakPriceDisplay;
            }
        }
        return "N/A";
    };

    const attributesInTable: AttributeTypeModel[] = [];
    const displayedAttributesProp = products[0].properties["DisplayedAttributes"];
    // if (displayedAttributesProp) {
    //     const displayedAttributes = products[0].properties["DisplayedAttributes"].split(",").map(o => o.trim());
    //     displayedAttributes.forEach(attribute => {
    //         const attributeType = products[0].attributeTypes?.find(o => o.name === attribute);
    //         if (attributeType) {
    //             attributesInTable.push(attributeType);
    //         }
    //     });
    // }

    // const displayedAttributes = ["Bundle_Qty_Packaging", "Style_Packaging"];
    // displayedAttributes.forEach(attribute => {
    //     const attributeType = products[0].attributeTypes?.find(o => o.name === attribute);
    //     if (attributeType) {
    //         attributesInTable.push(attributeType);
    //     }
    // });

    // XC Customization: Gets attribute values from attribute type
    const getAttributeValueForRow = (facet: AttributeTypeModel, product: ProductModel) => {
        const attributeValues = product.attributeTypes
            ?.find(o => o.id === facet.id)
            ?.attributeValues?.map(o => o.valueDisplay)
            .join(", ");
        if (attributeValues) {
            return attributeValues;
        }
        if (facet.name === "Brand" && product.brand) {
            return product.brand.name;
        }
        if (facet.name === "Product Line" && product.productLine) {
            return product.productLine.name;
        }

        return null;
    };

    const visibleTableColumns = tableColumns?.filter(o => visibleColumnNames.indexOf(o.name) > -1) || [];
    const productListRows = products.map((product, idx) => {
        const productInfo = productInfosByProductId[product.id];
        if (!productInfo) {
            return;
        }

        const productContext: ProductContextModel = {
            product,
            productInfo,
        };
        // const unitOfMeasure = productContext.product.unitOfMeasures?.find(
        //     o => o.unitOfMeasure === productContext.productInfo.unitOfMeasure,
        // );
        const productDetailPath = productInfo.productDetailPath ?? product.uri;

        return (
            <ProductListProductContext product={product} key={product.id} idx={idx}>
                <DataTableRow key={product.id} data-test-selector={`productListProductCard${product.id}`}>
                    <DataTableCell {...styles.descriptionCell}>
                        <Link
                            {...styles.productLink}
                            href={productDetailPath}
                            alt={`See product details for ${product.productNumber}`}
                            data-test-selector="productDescriptionLink"
                        >
                            {product.productNumber}
                        </Link>
                        {/* <ProductListProductInformation
                                    showAttributes={false}
                                    showAvailability={false}
                                    showBrand={showBrand}
                                    showPartNumbers={showPartNumbers}
                                    showTitle={showTitle}
                                    showImageBadges={showImageBadges}
                                    maxImageBadges={maximumNumberOfImageBadges}
                                    showTextBadges={showTextBadges}
                                    maxTextBadges={maximumNumberOfTextBadges}
                                />
                                <ProductListProductImage
                                    showImage={false}
                                    showCompare={true}
                                    extendedStyles={styles.productImageStyles}
                                />
                                {product.isSponsored && (
                                    <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>
                                )} */}
                    </DataTableCell>
                    {visibleTableColumns.map(facet => (
                        <DataTableCell
                            key={`${facet.name}_tableCell`}
                            {...styles.attributesCell}
                            data-test-selector={`attributeValuesFor${facet.attributeTypeId}`}
                        >
                            {getAttributeValueForSection(facet, product)}
                        </DataTableCell>
                    ))}
                    {/* {productSettings.showInventoryAvailability && showAvailability && (
                        <DataTableCell {...styles.availabilityCell}>
                            <ProductContextAvailability extendedStyles={styles.availabilityStyles} />
                        </DataTableCell>
                    )} */}
                    {/* {attributesInTable.map((attribute, index) => (
                        <DataTableCell key={`${attribute.name}_tableCell${index}`} {...styles.descriptionCell}>
                            {getAttributeValueForRow(attribute, product)}
                        </DataTableCell>
                    ))} */}
                    {priceBreakHeaders.map((priceBreak, index) => (
                        <DataTableCell key={`${priceBreak}_tableCell${index}`} {...styles.priceBreakCell}>
                            {getPriceBreaksForRow(priceBreak, product)}
                        </DataTableCell>
                    ))}
                    {priceBreakHeaders.length === 0 && <DataTableCell {...styles.priceBreakCell}></DataTableCell>}
                    {/* <DataTableCell {...styles.priceCell}>
                        <ProductPrice
                            product={productContext}
                            showLabel={false}
                            showUnitOfMeasure={false}
                            showPack={false}
                            showSavingsAmount={productSettings.showSavingsAmount}
                            showSavingsPercent={productSettings.showSavingsPercent}
                            extendedStyles={styles.productPrice}
                        />
                        <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                    </DataTableCell> */}
                    {/* <DataTableCell {...styles.unitOfMeasureCell}>
                        <Typography {...styles.unitOfMeasureText}>
                            {unitOfMeasure?.description || unitOfMeasure?.unitOfMeasureDisplay}
                        </Typography>
                    </DataTableCell> */}
                    <DataTableCell {...styles.actionsCell}>
                        <ProductListActions showAddToList={showAddToList} showPrice={false} />
                    </DataTableCell>
                </DataTableRow>
            </ProductListProductContext>
        );
    });

    return (
        <StyledWrapper {...styles.wrapper}>
            <DataTable {...styles.dataTable}>
                <thead>
                    <DataTableRow>
                        <DataTableHeader {...styles.descriptionHeader} rowSpan={priceHeaderRowSpan}>
                            {translate("Model No.")}
                        </DataTableHeader>
                        {visibleTableColumns.map(facet => (
                            <DataTableHeader
                                key={`${facet.name}_tableHeader`}
                                {...styles.attributesHeader}
                                rowSpan={priceHeaderRowSpan}
                            >
                                {facet.nameDisplay ||
                                    (facet.name === "Brand"
                                        ? translate("Brand")
                                        : facet.name === "Product Line"
                                        ? translate("Product Line")
                                        : facet.name)}
                            </DataTableHeader>
                        ))}
                        {/* {productSettings.showInventoryAvailability && (
                            <DataTableHeader {...styles.availabilityHeader}>{translate("Availability")}</DataTableHeader>
                        )} */}
                        {attributesInTable.map((attribute, index) => (
                            <DataTableHeader
                                key={`${attribute.name}_tableHeader${index}`}
                                {...styles.attributesHeader}
                                rowSpan={priceHeaderRowSpan}
                            >
                                {attribute.name}
                            </DataTableHeader>
                        ))}
                        {topPriceHeader}
                        {/* <DataTableHeader {...styles.priceHeader}>{translate("Price")}</DataTableHeader> */}
                        {/* <DataTableHeader {...styles.unitOfMeasureHeader}>{translate("U/M")}</DataTableHeader> */}
                        <DataTableHeader {...styles.actionsHeader} rowSpan={priceHeaderRowSpan}>
                            {translate("Add To Cart")}
                        </DataTableHeader>
                    </DataTableRow>
                    <DataTableRow>
                        {priceBreakHeaders.map((priceBreak, index) => (
                            <DataTableHeader
                                key={`${priceBreak}_tableHeader${index}`}
                                {...styles.priceBreakHeader}
                                rowSpan={1}
                            >
                                {priceBreak}
                            </DataTableHeader>
                        ))}
                    </DataTableRow>
                </thead>
                <DataTableBody>{productListRows}</DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(ProductListTable);
